import { RegisterFormFields } from '../hooks';
import { User } from '../model';
import { Agreement } from '../model/User';
import { HttpClient } from './HttpClient';

type MaybePrimitive = string | number | boolean | null | undefined;
type MaybeOption =
  | MaybePrimitive
  | { value?: any; label?: any; id?: any; name?: any; code?: any; key?: any; text?: any }
  | Array<any>;

/** Bezpieczne stringify z trimem dla prymitywów. Zwraca '' dla null/undefined. */
const s = (v: unknown): string =>
  v === undefined || v === null ? '' : String(v).trim();

/** Wyłuskuje wartość z przeróżnych kształtów selecta. Zwraca string, string[] lub ''. */
const pickSelect = (input: MaybeOption): string | string[] => {
  if (input == null) return '';

  if (Array.isArray(input)) {
    return input
      .map(pickSelect)
      .flat()
      .filter(v => v !== '') as string[];
  }

  if (typeof input !== 'object') {
    return String(input);
  }

  const obj = input as Record<string, any>;
  const candidates = [obj.value, obj.id, obj.code, obj.key, obj.label, obj.name, obj.text]
    .filter(v => v !== undefined && v !== null);

  const first = candidates[0];
  if (first && typeof first === 'object') {
    const nested = pickSelect(first);
    if (nested) return nested;
  }

  if (candidates.length > 0) {
    return String(candidates[0]);
  }

  try {
    return JSON.stringify(input);
  } catch {
    return String(input);
  }
};

/** Wrzuca prymityw do FormData, omija puste stringi. */
const put = (fd: FormData, key: string, val: unknown) => {
  const valStr = s(val);
  if (valStr !== '') fd.append(key, valStr);
};

/** Wrzuca select. Dla tablic buduje key[] powtarzane. */
const putSelect = (fd: FormData, key: string, input: MaybeOption) => {
  const val = pickSelect(input);
  if (Array.isArray(val)) {
    val.forEach(v => {
      if (v !== '') fd.append(`${key}[]`, v);
    });
  } else if (val !== '') {
    fd.append(key, val);
  }
};

/** Wrzuca plik jeśli istnieje. */
const putFile = (fd: FormData, key: string, f?: File | Blob | null) => {
  if (f) fd.append(key, f);
};

/** Bool na '1'/'0' */
const b01 = (v: unknown) => (v ? '1' : '0');

export class UserApi {
    /** Zwraca listę typów kart jako [{value,label}] pod select/radio */
  static async fetchCardTypes(): Promise<Array<{ value: string; label: string }>> {
    const { data } = await HttpClient.getInstance().get<Array<{ value: string; label: string }>>('/card-types');
    return data;
  }
  static async newsletter(enabled: boolean): Promise<User> {
    const formData = new FormData();
    formData.set('newsletter', enabled ? '1' : '0');
    const { data } = await HttpClient.getInstance().post<User>(
      '/newsletter',
      formData,
    );
    return data;
  }

  static async fetch(): Promise<User> {
    const { data } = await HttpClient.getInstance().get<User>('/self');
    return data;
  }

  static async rejectEdit(): Promise<User> {
    const { data } = await HttpClient.getInstance().get<User>('/reject-edit');
    return data;
  }

  static async edit(params: RegisterFormFields): Promise<void> {
    const fd = new FormData();

    // Imiona, nazwisko
    put(fd, 'first_name', params.firstName);
    put(fd, 'second_name', params.secondName);
    put(fd, 'last_name', params.lastName);

    // Obywatelstwo, PESEL
    // country może być prymitywem albo obiektem selecta
    putSelect(fd, 'citizenship', params.country as any);
    put(fd, 'pesel', params.pesel);

    // Data urodzenia tylko gdy kompletna
    const yyyy = s(params.birthYear);
    const mm = s(params.birthMonth).padStart(2, '0');
    const dd = s(params.birthDay).padStart(2, '0');
    if (yyyy && mm && dd) fd.append('birthdate', `${yyyy}-${mm}-${dd}`);

    // Kontakt
    put(fd, 'email', params.email);
    putSelect(fd, 'phone_prefix', params.phonePrefix as any);
    put(fd, 'phone', params.phoneNumber);

    // Adres
    putSelect(fd, 'country', params.country as any);
    put(fd, 'city', params.city);
    put(fd, 'street', params.street);
    put(fd, 'street_name', params.street_name);
    put(fd, 'building_number', params.building_number);
    put(fd, 'apartment_number', params.apartment_number);
    put(fd, 'post_code', params.postCode);

    // Województwo bywa stringiem, liczbą, obiektem, a czasem listą
    // Jeśli backend oczekuje pojedynczej wartości, weź pierwszy element z listy
    {
      const v = pickSelect((params as any).voivodeship);
      if (Array.isArray(v)) {
        if (v[0]) fd.append('voivodeship', v[0]);
      } else if (v !== '') {
        fd.append('voivodeship', v);
      }
    }

    // Flagi
    fd.append('residence_correspondence_address', '1');
    fd.append('newsletter', b01(params.newsletter));

    // Zdjęcie: preferuj przycięty blob, potem pierwszy plik
    const pictureFile =
      (params.photoCrop as File | Blob | undefined) ??
      (params.photo?.[0] as File | Blob | undefined);
    if (pictureFile && typeof pictureFile === 'object') {
      putFile(fd, 'picture', pictureFile);
    }

    // Załącznik do ulgi
    const attachment = params.attachment?.[0];
    if (attachment instanceof File) {
      putFile(fd, 'attachment', attachment);
    }

        // card_type jeżeli podano (wymuszamy string, bo FormData)
    if (params.card_type !== undefined && params.card_type !== null) {
      fd.append('card_type', String(params.card_type));
    }

    await HttpClient.getInstance().post('/edit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

    /** Minimalny endpoint do samej zmiany typu karty z modala */
  static async editCardType(cardType: number | string): Promise<void> {
    const fd = new FormData();
    fd.append('card_type', String(cardType));
    await HttpClient.getInstance().post('/edit-card-type', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static async editAgreements(params: AgreementPayload): Promise<number[]> {
    const fd = new FormData();

    // tylko ID z zaznaczonych
    params.agreements
      .filter(a => !!a.has_agree)
      .forEach(a => fd.append('agreements[]', String(a.id)));

    const { data } = await HttpClient.getInstance().post<number[]>('/edit-agreements', fd);
    return data;
  }
}

export interface AgreementPayload {
  agreements: Agreement[];
}